import { z } from 'zod';

/** claude-cli: Claude Code sul server con l'abbonamento Claude (niente costi a consumo). */
export const AI_PROVIDERS = ['claude-cli', 'anthropic', 'deepseek'] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];

/** Modelli selezionabili (Claude Opus è il predefinito). */
export const AI_MODELS: Record<AiProvider, Array<{ id: string; label: string }>> = {
  'claude-cli': [
    { id: 'opus', label: 'Claude Opus (il più capace)' },
    { id: 'sonnet', label: 'Claude Sonnet (più veloce, consuma meno limiti)' },
  ],
  anthropic: [
    { id: 'claude-opus-5-5', label: 'Claude Opus 5.5 (il più capace)' },
    { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5 (più economico)' },
    { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5 (il più economico)' },
  ],
  deepseek: [
    { id: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro' },
    { id: 'deepseek-flash', label: 'DeepSeek Flash' },
  ],
};

export interface AiConfig {
  provider: AiProvider;
  model: string;
  monthlyBudgetEur: number;
  enabled: boolean;
  hasAnthropicKey: boolean;
  hasDeepseekKey: boolean;
  /** Claude Code sul server è collegato all'account Claude. */
  hasClaudeLogin: boolean;
  monthCostEur: number;
}

export const aiConfigSchema = z.object({
  provider: z.enum(AI_PROVIDERS).optional(),
  model: z.string().max(60).optional(),
  monthlyBudgetEur: z.number().min(0).max(1000).optional(),
  enabled: z.boolean().optional(),
  anthropicKey: z.string().trim().max(300).nullable().optional(),
  deepseekKey: z.string().trim().max(300).nullable().optional(),
});
export type AiConfigInput = z.infer<typeof aiConfigSchema>;

export interface AiUsageRow {
  day: string;
  feature: string;
  costEur: number;
  calls: number;
}

export interface InsightFinding {
  title: string;
  detail: string;
  tone: 'good' | 'bad' | 'neutral';
}

export interface InsightAction {
  title: string;
  why: string;
  /** Giorni entro cui farla, se ha senso. */
  dueInDays: number | null;
  taskId: string | null;
}

export interface Insight {
  id: string;
  pageId: string;
  summary: string;
  findings: InsightFinding[];
  actions: InsightAction[];
  model: string;
  createdAt: string;
  seenAt: string | null;
}

export interface AssistantMessageView {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** Strumenti usati in questo passaggio (per mostrare "ho controllato i numeri…"). */
  tools: Array<{ name: string; label: string }>;
  createdAt: string;
}

export interface PendingAction {
  toolUseId: string;
  name: string;
  label: string;
  /** Descrizione leggibile di cosa verrà fatto. */
  summary: string;
  input: Record<string, unknown>;
}

export interface Conversation {
  id: string;
  title: string;
  pageId: string | null;
  updatedAt: string;
  messages: AssistantMessageView[];
  pending: PendingAction | null;
}

export interface ConversationSummary {
  id: string;
  title: string;
  pageId: string | null;
  updatedAt: string;
}

export const claudeLoginCodeSchema = z.object({
  code: z.string().trim().min(10, 'Incolla il codice che ti ha dato Claude').max(400),
});

/** Token consumati in un periodo (letti dalla cache contati a parte: pesano molto meno). */
export interface AiTokenWindow {
  input: number;
  output: number;
  cacheRead: number;
  /** input + output + letti dalla cache */
  total: number;
  requests: number;
}

export interface AiTokenPeriods {
  today: AiTokenWindow;
  week: AiTokenWindow;
  month: AiTokenWindow;
}

/** Consumo di una scheda (cioè del suo agente), o di quello che non riguarda nessuna scheda (pageId null). */
export interface AiTokenRow extends AiTokenPeriods {
  pageId: string | null;
  pageName: string;
  color: string | null;
  /** Totali del mese per tipo: giri e attività dell'agente, chat, analisi e bozze. */
  monthByKind: { lavoro: number; chat: number; analisi: number };
}

export interface AiTokens {
  totals: AiTokenPeriods;
  pages: AiTokenRow[];
}
